import { useContext, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { enqueueSnackbar } from 'notistack'

import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'

import { AppContext } from '/providers/app-context'
import { deleteTemplate } from '/providers/templates-api'

const DeleteDialog = ({ open, setOpen, template }) => {
  const { apiSource, apiKey, refreshTemplates, processing, setProcessing } = useContext(AppContext)
  const navigate = useNavigate()
  const [livePassesFound, setLivePassesFound] = useState(null)
  const [livePassConfirmation, setLivePassConfirmation] = useState('')

  if (!template) return null

  const close = () => {
    if (processing) return
    setLivePassesFound(null)
    setLivePassConfirmation('')
    setOpen(false)
  }

  const doDelete = async (deletePasses = false) => {
    setProcessing(true)
    enqueueSnackbar({ message: deletePasses ? 'Deleting passes and template' : 'Checking template passes', variant: 'light' })
    const result = await deleteTemplate({ apiSource, apiKey, templateId: template.template_id, deletePasses })

    if (result?.passes_exist) {
      setLivePassesFound(result.live_pass_count)
      setProcessing(false)
      return
    }

    if (result?.error || result?.ok === false) {
      enqueueSnackbar({ message: result?.error?.message ?? 'Unable to delete template', variant: 'error' })
      setProcessing(false)
      return
    }

    enqueueSnackbar({ message: 'Template deleted', variant: 'success' })
    setProcessing(false)
    setLivePassesFound(null)
    setLivePassConfirmation('')
    setOpen(false)
    await refreshTemplates()
    navigate('/templates')
  }

  return (
    <Dialog open={open} onClose={close} aria-labelledby="delete-template-title">
      <DialogTitle id="delete-template-title">
        {livePassesFound === null ? `Delete template: ${template.name}` : 'Delete live passes and template?'}
      </DialogTitle>
      <DialogContent>
        {livePassesFound === null
          ? <>
              <p>This permanently deletes the archived template.</p>
              <p>Any associated test passes will also be deleted.</p>
            </>
          : <>
              <p>This template has {livePassesFound} live {livePassesFound === 1 ? 'pass' : 'passes'}.</p>
              <p>Deleting the template will also permanently delete all of its live and test passes. Enter the number of live passes to confirm.</p>
              <TextField
                autoFocus
                fullWidth
                label="Live passes to delete"
                value={livePassConfirmation}
                onChange={event => setLivePassConfirmation(event.target.value)}
                inputProps={{ inputMode: 'numeric' }}
                error={livePassConfirmation !== '' && livePassConfirmation !== String(livePassesFound)}
                helperText={livePassConfirmation !== '' && livePassConfirmation !== String(livePassesFound) ? `Enter ${livePassesFound}` : ''}
              />
            </>}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={close} disabled={processing}>Cancel</Button>
        <Button variant="contained" color="error" onClick={() => doDelete(livePassesFound !== null)} disabled={processing || (livePassesFound !== null && livePassConfirmation !== String(livePassesFound))} autoFocus>
          <DeleteOutlinedIcon sx={{ mr: 1 }} />
          {livePassesFound === null ? 'Delete template' : 'Delete passes and template'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default DeleteDialog
